import { UseQueryResult } from '@tanstack/react-query'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import {
  WidgetCardBody,
  WidgetCardHeader,
  getProgressBarColor,
} from '@pages/Trade/Analytics/Analytics.components'
import ShowMoreModal from '@pages/Trade/Analytics/Analytics.modal'
import { WidgetCardProps } from '@pages/Trade/Analytics/Analytics.types'
import {
  AnalyticsFilters,
  FiltersRow,
  initialFiltersQuery,
} from '@pages/Trade/Analytics/components/FiltersRow'
import { TopAnalyticsItem, useAnalyticsFetch } from '@pages/Trade/Analytics/hooks/useAnalyticsFetch'
import { useCollapsibleFilters } from '@pages/Trade/Analytics/hooks/useCollapsibleFilters'
import {
  MarketDataBridge,
  updateCurrentPricesFromMarketData,
} from '@pages/Trade/Portfolio/components/MarketDataBridge'

import { useModal } from '@context/ModalsContext'

import { Grid } from '@ui/Layouts/Grid/Grid'
import { Title1, Title2 } from '@ui/Typography'
import { PageSection } from '@ui/components/PageSection'
import { ChartPieIcon } from '@ui/icons/ChartPie.icon'
import { LogoIcon } from '@ui/icons/LogoIcon'

import { useWindowResize } from '@utils/domUtils'

import { useLocallyPersistedState } from '@hooks/useStorage'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'

import styles from './Analytics.page.module.scss'

export const AnalyticsPage = () => {
  const { t } = useTranslation()

  const [filters, setFilters] = useLocallyPersistedState<AnalyticsFilters>(
    'analytics-filters',
    initialFiltersQuery()
  )

  const { filtersVisible, onFilterToggle } = useCollapsibleFilters()
  const data = useAnalyticsFetch(filters)

  return (
    <div className={styles.analytics}>
      <MarketDataBridge symbols={data.symbols} onTick={updateCurrentPricesFromMarketData} />
      <PageSection title={t('Tabs.Analytics')} onFilterClick={onFilterToggle} icon={ChartPieIcon} />
      <Box flex align='center' gap='x12' wrap>
        {filtersVisible && <FiltersRow filters={filters} onFiltersChange={setFilters} />}
      </Box>
      <Box>
        {data.isFetching ? (
          <Loading isLoading={data.isFetching} showLoadingIcon />
        ) : (
          <Widgets data={data} />
        )}
      </Box>
    </div>
  )
}

interface WidgetsProps {
  data: ReturnType<typeof useAnalyticsFetch>
}

const Widgets: React.FC<WidgetsProps> = ({ data }) => {
  const { t } = useTranslation()

  const [moversTab, setMoversTab] = useState<'risers' | 'fallers'>('risers')
  const [tradedTab, setTradedTab] = useState<'bought' | 'sold'>('bought')

  return (
    <Box flex direction='column' gap='x24'>
      <Grid
        maxCardWidth={365}
        mobileSameSize
        mobileGridTemplateColumns='minmax(0, 1fr)'
        className={styles.widgets}
      >
        <WidgetCard
          title={t('Analytics.Top Movers')}
          activeTab={moversTab}
          tabs={[
            {
              id: 'risers',
              name: t('Analytics.Risers'),
              query: data.risers,
              onClick: () => setMoversTab('risers'),
            },
            {
              id: 'fallers',
              name: t('Analytics.Fallers'),
              query: data.fallers,
              onClick: () => setMoversTab('fallers'),
            },
          ]}
        />
        <WidgetCard
          title={t('Analytics.Top Traded')}
          activeTab={tradedTab}
          tabs={[
            {
              id: 'bought',
              name: t('Analytics.Bought'),
              query: data.bought,
              onClick: () => setTradedTab('bought'),
            },
            {
              id: 'sold',
              name: t('Analytics.Sold'),
              query: data.sold,
              onClick: () => setTradedTab('sold'),
            },
          ]}
        />
        <WidgetCard title={t('Analytics.Most Volatile')} query={data.volatile} />
        <WidgetCard title={t('Analytics.Most Followed')} query={data.followed} />
      </Grid>
    </Box>
  )
}

const WidgetCard: React.FC<WidgetCardProps> = (props) => {
  const { title, activeTab, tabs = [], query } = props

  const navigate = useNavigate()
  const isMobile = useWindowResize()

  const activeTabData = tabs.find((tab) => tab.id === activeTab)
  const selectedQuery = activeTabData?.query ?? query

  const handleShowMore = () => {
    showMoreModal.open()
  }

  const handleInstrumentClick = useCallback(
    (symbol: string) => {
      if (isMobile) {
        navigate(`/dashboard/trade/markets?symbol=${symbol}&tab=chart`, {
          state: { isInstrumentRedirect: true },
        })
      } else {
        navigate('/dashboard/trade/markets', {
          state: { symbol, isInstrumentRedirect: true },
        })
      }
    },
    [navigate, isMobile]
  )

  const showMoreModal = useModal(ShowMoreModal, {
    title,
    activeTab,
    tabs,
    query,
    onClick: handleInstrumentClick,
  })

  if (!hasData(selectedQuery)) {
    return <WidgetCardEmpty {...props} />
  }

  return (
    <Box flex direction='column' justify='flex-start' gap='x4' radius='x24' border='outline-low-em'>
      <Box flex direction='column'>
        <Box flex align='center'>
          <WidgetCardHeader
            title={title}
            activeTab={activeTab}
            tabs={tabs}
            onClick={handleShowMore}
          />
        </Box>
        <Box px='x20' pb='x20'>
          <WidgetCardBody
            data={selectedQuery.data}
            limit={3}
            progressBarColor={getProgressBarColor(activeTab)}
            onInstrumentClick={handleInstrumentClick}
          />
        </Box>
      </Box>
    </Box>
  )
}

const hasData = (
  query?: UseQueryResult<TopAnalyticsItem[], Error>
): query is UseQueryResult<TopAnalyticsItem[], Error> & { data: TopAnalyticsItem[] } => {
  return !!query && !query.isError && Array.isArray(query.data) && query.data.length > 0
}

const WidgetCardEmpty: React.FC<WidgetCardProps> = (props) => {
  const { t } = useTranslation()

  return (
    <Box flex direction='column' justify='flex-start' gap='x4' radius='x24' border='outline-low-em'>
      <Box flex direction='column'>
        <Box flex align='center'>
          <WidgetCardHeader {...props} />
        </Box>
        <Box px='x20' pt='x12' pb='x20'>
          <Box textAlign='center' pb='x12'>
            <LogoIcon color='text-med-em' secondaryColor='text-med-em' size={48} />
          </Box>
          <Box textAlign='center' pb='x6'>
            <Title1 medium color='text-high-em'>
              {t('Analytics.No data yet')}
            </Title1>
          </Box>
          <Box textAlign='center'>
            <Title2 color='text-low-em'>
              {t('Analytics.We’ll highlight the most important market trends here as they happen')}
            </Title2>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
